import { createStore } from "./store.ts";

/**
 * THE SESSION, AS A STATE RATHER THAN AN ERROR.
 *
 * Expiry is the single most common thing that happens to a signed-in user, and modelling it as a failure at
 * the moment of action is how it goes wrong: the Buy button throws a raw vendor message and the user learns
 * they are signed out by pressing a button that does nothing, twice. Here it is a FIRST-CLASS STATE with its own copy,
 * its own recovery and — critically — its own guarantee that the panel's amount survives it.
 *
 * THREE CREDENTIALS, AND CONFLATING ANY TWO IS A CUSTODY ERROR (`lifecycle.md` §0.3):
 *
 *   the wallet record   `localStorage`, inert. An email, a sub-org id and an address. IT CONFERS NOTHING.
 *   the SIGNING session the SDK's IndexedDB keystore, non-extractable. The only thing that can SIGN.
 *   the API session     `sessionStorage`, one bearer per tab. Tells our backend WHICH WALLET is calling.
 *
 * A live API session DOES NOT IMPLY the ability to sign: they are two clocks that expire independently, and
 * `signer` below is the answer to the second question, obtained by a REAL keystore probe. The Buy button is
 * gated on BOTH, and never on a persisted boolean — a flag written at sign-in survives session expiry, a
 * cleared keystore and any deploy that changed how sessions are stored.
 */

/** Why the user is signed out. Each one has a DIFFERENT message because each has a different cause and a
 *  different fix, and "you were signed out" for all four is the copy that produces "it logged me out for no
 *  reason" support tickets. A CLOSED UNION because it is also a metric label. */
export type SignedOutReason =
  | "never" // has not signed in on this device
  | "expired" // the absolute deadline passed (≤6 h, possibly clamped shorter)
  | "idle" // 60 min with no authenticated request — a backgrounded tab's usual fate
  | "revoked" // signed out elsewhere, or the per-user cap
  | "signed_out"; // the user asked

export const SIGNED_OUT_COPY: Readonly<Record<SignedOutReason, string>> = {
  never: "Sign in to trade.",
  expired: "Your session expired — sign in to continue.",
  idle: "Signed out — inactive.",
  revoked: "You were signed out.",
  signed_out: "Signed out.",
};

/** Whether THIS BROWSER can sign. `null` = not probed yet, and it is rendered as "checking", never as "no":
 *  a false negative here disables the Buy button for a user who can trade. */
export type SignerState = boolean | null;

export interface SessionView {
  /** `"unknown"` is the state before the first `/auth/session` answers, and it is DISTINCT from signed-out:
   *  a returning tab renders its masked address optimistically from a local bearer and corrects ~40 ms later,
   *  which is one transition instead of a guaranteed flash of "Sign in" for every returning user. */
  status: "unknown" | "signedIn" | "signedOut";
  reason: SignedOutReason;
  address: string | null;
  subOrgId: string | null;
  /** Wall-clock ms. Shown, not discovered — the user is told how long they have rather than finding out.
   *  ABSOLUTE: the server has no sliding refresh, so this is the whole story. */
  expiresAt: number | null;
  /** ⚠️ NOT SERVED TODAY. `SessionResponse` carries no idle deadline, so the client cannot tell a user that an
   *  inactive tab will be signed out — it can only report it after the fact, from a 401. Kept in the shape
   *  because the copy for it already exists and the field is a server addition, not a client one. */
  idleExpiresAt: number | null;
  signer: SignerState;
}

const EMPTY: SessionView = {
  status: "unknown",
  reason: "never",
  address: null,
  subOrgId: null,
  expiresAt: null,
  idleExpiresAt: null,
  signer: null,
};

export const sessionStore = createStore<SessionView>(EMPTY);

export function setSignedIn(v: {
  address: string;
  /** Null for an external wallet: its key lives in an extension, not in a turnkey sub-organization. */
  subOrgId: string | null;
  expiresAt: number;
  idleExpiresAt?: number | null;
}): void {
  sessionStore.set((prev) => ({
    ...prev,
    status: "signedIn",
    reason: "never",
    address: v.address,
    subOrgId: v.subOrgId,
    expiresAt: v.expiresAt,
    idleExpiresAt: v.idleExpiresAt ?? null,
  }));
}

/** The address and sub-org SURVIVE a sign-out-by-expiry: they are the inert wallet record, they are what the
 *  re-sign-in needs, and clearing them would send the user back through account creation — which spends a
 *  metered vendor operation for a session that merely aged out. Only an explicit sign-out clears them. */
export function setSignedOut(reason: SignedOutReason, forget = false): void {
  sessionStore.set((prev) => ({
    ...prev,
    status: "signedOut",
    reason,
    expiresAt: null,
    idleExpiresAt: null,
    ...(forget ? { address: null, subOrgId: null, signer: false } : {}),
  }));
}

export function setSignerState(signer: SignerState): void {
  sessionStore.set((prev) => (prev.signer === signer ? prev : { ...prev, signer }));
}

/** The wallet record, restored before the first paint so a returning tab does not flash "Sign in". */
export function setKnownWallet(address: string, subOrgId: string): void {
  sessionStore.set((prev) => ({ ...prev, address, subOrgId }));
}

/**
 * THE ONE GATE THE BUY BUTTON AND THE WITHDRAW BUTTON READ.
 *
 * Both credentials, and the signer probe must have COMPLETED. `signer === null` is "checking", which renders
 * as a disabled button with "checking your signing session…" rather than as a refusal — the two are different
 * to a user and identical to a naive boolean.
 */
export function canSign(v: SessionView): boolean {
  return v.status === "signedIn" && v.signer === true;
}
